// src/components/InventariosActivos.js
import React, { useState, useEffect, useCallback } from "react";
// Importa BiCheckDouble junto con los otros íconos
import { 
  BiBox, BiCheck, BiArrowBack, BiCheckDouble, BiDownload, BiSearch,
  BiBarChart, BiTargetLock, BiTrendingDown, BiTimeFive, BiDollarCircle,
  BiLoaderAlt
} from "react-icons/bi";
import "./InventariosActivos.css";
import InventoryAnalytics from "./InventoryAnalytics";
import { computeAssertiveness } from "../../../utils/assertiveness";

// ... (ProductTable logic) ...
const ProductTable = ({
  products,
  onBack,
  viewTitle,
  columns,
  onAdjustLineClick,
  showAdjustButton,
  onDownload, // Nueva prop
}) => (
  <div className="product-table-container">
    <div className="product-table-header">
      <div className="header-left">
        <button onClick={onBack} className="back-button-table">
          <BiArrowBack /> Volver
        </button>
      </div>
      {/*<div className="header-left-search">
        <div className="search-box">
          <input
            //value={claveBuscada}
            //onChange={onChange}
            type="text"
            placeholder="Buscar"
          />
          <BiSearch style={{ color: "#969393", fontSize: "1.4rem" }} />
        </div>
      </div>*/}
      <div className="header-center">
        <h3>{viewTitle}</h3>
      </div>
      <div className="header-right">
        {showAdjustButton && (
          <button
            className="adjust-line-table-button"
            onClick={onAdjustLineClick}
            title="Marcar línea como ajustada en sistema"
          >
            Línea Ajustada
          </button>
        )}
        {viewTitle === "Todos los Productos Contados" ? (
          <button
            onClick={onDownload}
            className="download-button"
            title="Descargar"
          >
            {" "}
            <BiDownload style={{ fontSize: "1.3em" }} /> Descargar
          </button>
        ) : null}
      </div>
    </div>
    {products.length > 0 ? (
      <div className="table-scroll-wrapper">
        {" "}
        {/* Contenedor para el scroll */}
        <table className="product-table">
          <thead>
            <tr>
              {columns.map((col, index) => (
                <th key={index}>{col.header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {products.map((product, index) => (
              <tr key={index}>
                {columns.map((col, colIndex) => (
                  <td key={colIndex}>{product[col.accessor]}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : (
      <div className="no-data-message">No hay productos para mostrar.</div>
    )}
  </div>
);

// Definición de columnas
const BASIC_PRODUCT_COLUMNS = [
  { header: 'Clave', accessor: 'Clave' },
  { header: 'Descripción', accessor: 'Descripcion' },
  { header: 'Unidad', accessor: 'Unidad' },
];

const COUNTED_LINE_PRODUCT_COLUMNS = [
  { header: 'Clave', accessor: 'Clave' },
  { header: 'Descripción', accessor: 'Descripcion' },
  { header: 'Unidad', accessor: 'Unidad' },
  { header: 'Existencia', accessor: 'qtyConteo' },
];

const ALL_COUNTED_PRODUCTS_COLUMNS = [
  { header: 'Clave', accessor: 'Clave' },
  { header: 'Descripción', accessor: 'Descripcion' },
  { header: 'Línea', accessor: 'Linea' },
  { header: 'Existencia', accessor: 'qtyConteo' }, // Corrected accessor
  { header: 'Caja', accessor: 'Caja' },
  { header: 'Unidad', accessor: 'Unidad' },
  { header: 'Observaciones', accessor: 'Observaciones' },
];

const LineaCard = ({ linea, onClick }) => {
  const { Linea, NombreLinea, qtyProductosLinea, isCounted, isAdjusted } = linea;

  // Lógica para aplicar la clase CSS correctamente
  let cardClassName = "linea-card";
  if (isAdjusted) {
      cardClassName += " adjusted"; // Si está ajustada, solo aplica la clase 'adjusted'
  } else if (isCounted) {
      cardClassName += " counted"; // Si no está ajustada pero sí contada, aplica la clase 'counted'
  }

  // Lógica para definir el texto del tooltip
  let tooltipText = "";
  if (isAdjusted) {
    tooltipText = "Línea ajustada en sistema";
  } else if (isCounted) {
    tooltipText = "Línea cuantificada";
  } else {
    tooltipText = "Línea pendiente de cuantificar";
  }

  return (
    // Condicionamos el onClick: si isAdjusted es true, no se asigna la función onClick.
    <div
      className={cardClassName}
      onClick={isAdjusted ? null : () => onClick(linea)} // La tarjeta no es clickeable si está ajustada
      title={tooltipText} // Añadimos el tooltip
    >
      <div className="linea-info">
        <h4>{Linea}</h4>
        <p>{NombreLinea}</p>
        <p>Productos: {qtyProductosLinea}</p>
      </div>
      {/* Lógica para mostrar los íconos */}
      {isAdjusted ? (
        <BiCheckDouble className="icon-check" size={30} /> // Ícono BiCheckDouble si está ajustada
      ) : isCounted ? (
        <BiCheck className="icon-check" size={30} /> // Ícono BiCheck si está contada pero no ajustada
      ) : null}
    </div>
  );
};

const MetricCard = ({ label, value, icon, colorClass, onClick, isActive, loading, style }) => (
  <div 
    className={`metric-card ${onClick ? 'interactive' : ''} ${isActive ? 'active-filter' : ''} ${loading ? 'disabled' : ''}`} 
    onClick={loading || !onClick ? undefined : onClick}
    style={style}
  >
    <div className={`metric-icon-container ${colorClass}`}>
      {icon}
    </div>
    <div className="metric-content">
      <span className="metric-label">{label}</span>
      {loading ? (
        <div className="metric-spinner-wrapper">
          <BiLoaderAlt className="metric-spinner" />
          <span className="metric-spinner-text">Cargando...</span>
        </div>
      ) : (
        <span className="metric-value">{value}</span>
      )}
    </div>
  </div>
);

const formatMoney = (n) => {
  const abs = Math.abs(n).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n >= 0 ? `+$${abs}` : `-$${abs}`;
};

const resolveInvId = (id) => id === "052026-1" ? "INVMAY" : id;

const InventarioDetails = ({ inventario, onBack }) => {
  const [viewMode, setViewMode] = useState('lines'); // 'lines', 'lineProducts', 'allProducts', 'analytics'
  const [lineas, setLineas] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedLinea, setSelectedLinea] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false); // State for modal visibility
  const [claveBuscada, setClaveBuscada] = useState("");
  const [assertivenessData, setAssertivenessData] = useState(null);
  const [assertivenessRawData, setAssertivenessRawData] = useState(null);
  const [assertivenessLoading, setAssertivenessLoading] = useState(false);
  const [activeMetric, setActiveMetric] = useState(null);
  const [efficiencyData, setEfficiencyData] = useState(null);
  const [efficiencyLoading, setEfficiencyLoading] = useState(false);

  const handleOpenMetric = (metric) => {
    setActiveMetric(metric);
    if (metric === 'progreso') {
      handleViewAllProducts();
    } else if (metric === 'magnitud') {
      setViewMode('magnitud');
    } else if (metric === 'eficiencia') {
      setViewMode('eficiencia');
    } else if (metric === 'diferencias') {
      setViewMode('diferencias');
    } else if (metric === 'bi') {
      setViewMode('bi');
    } else {
      setViewMode('analytics');
    }
  };

  const fetchAssertiveness = useCallback(async () => {
    setAssertivenessLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_URL_API1}/dashboard/asertividad?inventarioId=${resolveInvId(inventario.InventarioID)}`
      );
      if (!response.ok) throw new Error('Error al cargar asertividad');
      const data = await response.json();
      setAssertivenessRawData(data);
      const computed = computeAssertiveness(data);
      setAssertivenessData(computed);
    } catch (e) {
      console.error("Error al cargar asertividad:", e);
    } finally {
      setAssertivenessLoading(false);
    }
  }, [inventario.InventarioID]);

  const fetchEfficiency = useCallback(async () => {
    setEfficiencyLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_URL_API1}/dashboard/sku-hr?inventarioId=${resolveInvId(inventario.InventarioID)}`
      );
      if (!response.ok) throw new Error('Error al cargar eficiencia');
      const data = await response.json();
      setEfficiencyData(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Error al cargar eficiencia:", e);
      setEfficiencyData([]);
    } finally {
      setEfficiencyLoading(false);
    }
  }, [inventario.InventarioID]);

  const refreshLineas = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_URL_API1}/getlineasinvresumen?InventarioID=${inventario.InventarioID}&auditor=${inventario.Auditor}`
      );
      if (!response.ok) throw new Error('Error al cargar líneas');
      let data = await response.json();

      if (Array.isArray(data)) {
        data.sort((a, b) => {
          const nameA = a.NombreLinea ? a.NombreLinea.toUpperCase() : '';
          const nameB = b.NombreLinea ? b.NombreLinea.toUpperCase() : '';
          if (nameA < nameB) return -1;
          if (nameA > nameB) return 1;
          return 0;
        });
        setLineas(data);
      } else {
        setLineas([]);
      }
    } catch (e) {
      setError("No se pudieron cargar las líneas.");
    } finally {
      setLoading(false);
    }
  }, [inventario.InventarioID, inventario.Auditor]); // Dependencies for useCallback

  // Fetch inicial para las líneas del inventario al montar o cambiar inventario
  useEffect(() => {
    refreshLineas();
  }, [refreshLineas]); // Depend on refreshLineas to re-run when its dependencies change

  // Fetch asertividad al montar el detalle
  useEffect(() => {
    fetchAssertiveness();
    fetchEfficiency();
  }, [fetchAssertiveness, fetchEfficiency]);

  // Handler para ver todos los productos
  const handleViewAllProducts = async () => {
    setViewMode('allProducts');
    setLoading(true);
    setError(null);
    try {
      // Obtiene todo el universo de productos contados de un inventario
      const response = await fetch(
        `${process.env.REACT_APP_URL_API2}/api/v1/productoscontados/inventario/${inventario.InventarioID}/auditor/${encodeURIComponent(inventario.Auditor)}`
      );
      if (response.status === 404) {
        setProducts([]);
        return;
      }
      if (!response.ok) throw new Error('Error al cargar productos');
      const payload = await response.json();
      const data = payload.body || payload;
      const mappedProducts = Array.isArray(data) ? data.map(p => ({
        Clave: p.Clave,
        Descripcion: p.Descripcion,
        Linea: p.Linea,
        qtyConteo: p.Existencia, // Mapeado a Existencia
        Caja: p.Caja,
        Unidad: p.Unidad,
        Observaciones: p.Observaciones // Nueva columna
      })) : [];
      console.log(mappedProducts);
      setProducts(mappedProducts);
    } catch (e) {
      setError("No se pudieron cargar todos los productos contados.");
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  // Handler para ver productos de una línea específica
  const handleLineaClick = async (linea) => {
    setSelectedLinea(linea);
    setViewMode('lineProducts');
    setLoading(true);
    setError(null);
    try {
      let url;
      let mappedProducts;
      if (linea.isCounted) {
        
        url = `${process.env.REACT_APP_URL_API1}/getproductoscontadosporauditorporlineaeinv?InventarioID=${inventario.InventarioID}&auditor=${inventario.Auditor}&Linea=${linea.Linea}`;
        const response = await fetch(url);
        if (!response.ok) throw new Error('Error al cargar productos de la línea');
        const data = await response.json();
        mappedProducts = Array.isArray(data) ? data.map(p => ({
          Clave: p.Clave,
          Descripcion: p.Descripcion,
          qtyConteo: p.Existencia,
          Caja: p.Caja,
          Unidad: p.Unidad,
        })) : [];
      } else {
        url = `${process.env.REACT_APP_URL_API1}/getproductosporlineaeinv?InventarioID=${inventario.InventarioID}&Linea=${linea.Linea}&auditor=${inventario.Auditor}`;
        const response = await fetch(url);
        if (!response.ok) throw new Error('Error al cargar productos de la línea');
        const data = await response.json();
        mappedProducts = Array.isArray(data) ? data.map(p => ({
          Clave: p.Clave,
          Descripcion: p.Descripcion,
          Unidad: p.Unidad,
        })) : [];
      }

      setProducts(mappedProducts);
    } catch (e) {
      setError("No se pudieron cargar los productos de la línea.");
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const resetView = () => {
    setViewMode('lines');
    setSelectedLinea(null);
    setProducts([]);
    setError(null);
    setActiveMetric(null);
    refreshLineas();
  };

  // ... (Adjustment handlers remain the same) ...
  // Handler para mostrar el modal de confirmación
  const handleLineaAjustada = () => {
    setShowConfirmModal(true);
  };

  // Handler para cancelar en el modal
  const handleCancelAdjustment = () => {
    setShowConfirmModal(false);
    // No navigation or other actions here, just close the modal.
  };

  // Handler para aceptar en el modal y descargar la tabla + llamar a la API
  const handleConfirmAdjustment = async () => {
    setShowConfirmModal(false); // Close the modal

    // 1. Descargar el contenido de la tabla
    exportTableDataToCsv();

    // 2. Llamar a la API para marcar la línea como ajustada
    if (selectedLinea) {
      const payload = {
        InventarioID: inventario.InventarioID,
        Linea: selectedLinea.Linea,
        NombreLinea: selectedLinea.NombreLinea, // Assuming NombreLinea is available on selectedLinea
        isAdjusted: true,
        Auditor: inventario.Auditor,
      };

      try {
        const response = await fetch(`${process.env.REACT_APP_URL_API2}/api/v1/lineaajustada`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error(`Error al ajustar la línea: ${response.statusText}`);
        }

        const result = await response.json();
        console.log('Línea ajustada con éxito en el sistema:', result);

        // Actualizar el estado de la línea localmente, incluyendo isAdjusted
        setLineas(prevLineas => prevLineas.map(line =>
          line.Linea === selectedLinea.Linea ? { ...line, isCounted: true, isAdjusted: true } : line
        ));


      } catch (apiError) {
        console.error('Error al llamar a la API de ajuste de línea:', apiError);
        alert(`Error al ajustar la línea: ${apiError.message}. Por favor, inténtalo de nuevo.`);
      }
    } else {
      console.warn("No hay línea seleccionada para ajustar.");
    }
    console.log(`Línea ${selectedLinea?.Linea} marcada para ajuste y datos descargados.`);

    resetView(); // Navigate back to the previous view after actions
  };

  // ... (exportTableDataToCsv remains the same) ...
  // Function to export table data to CSV
  const exportTableDataToCsv = () => {
    if (!products || products.length === 0) {
      alert("No hay datos para exportar.");
      return;
    }

    // Determine columns based on selectedLinea context
    const exportColumns = selectedLinea?.isCounted ? COUNTED_LINE_PRODUCT_COLUMNS : BASIC_PRODUCT_COLUMNS;
    if (viewMode === 'allProducts') {
      // Use ALL_COUNTED_PRODUCTS_COLUMNS if viewing all products
      const allCountedProductsView = products.every(p => p.Observaciones !== undefined);
      if (allCountedProductsView) {
        exportColumns.splice(0, exportColumns.length, ...ALL_COUNTED_PRODUCTS_COLUMNS); // Efficiently replace array content
      }
    }

    const header = exportColumns.map(col => col.header).join(',');
    const rows = products.map(row =>
      exportColumns.map(col => {
        const value = row[col.accessor];
        // Enclose values with commas or newlines in quotes
        // Also, convert value to string to ensure replace method works
        return typeof value === 'string' && (value.includes(',') || value.includes('\n') || value.includes('"'))
          ? `"${value.replace(/"/g, '""')}"` // Escape double quotes
          : String(value); // Convert to string for consistent output
      }).join(',')
    );

    const csvContent = [header, ...rows].join('\n');
    // Prepend the BOM to the CSV content to ensure correct UTF-8 interpretation by Excel
    const BOM = "\ufeff"; // UTF-8 Byte Order Mark
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const fileName = `Linea_${selectedLinea?.Linea || 'TodosProductos'}_${inventario.InventarioID}.csv`;

    // Create a temporary link element to trigger the download
    const link = document.createElement("a");
    if (link.download !== undefined) { // Feature detection for download attribute
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", fileName);
      link.style.visibility = 'hidden'; // Hide the link
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url); // Clean up the URL object
    } else {
      alert("Su navegador no soporta la descarga directa. Por favor, copie el contenido.");
      console.log(csvContent);
    }
    console.log("Datos de la tabla descargados como CSV.");
  };

  const renderContent = () => {
    if (loading) return <div className="loading-message">Cargando...</div>;
    if (error) return <div className="error-message">{error}</div>;

    switch (viewMode) {
      case 'analytics':
      case 'magnitud':
      case 'eficiencia':
      case 'diferencias':
      case 'bi':
        return (
          <InventoryAnalytics 
            mode={viewMode}
            onClose={() => { setViewMode('lines'); setActiveMetric(null); }}
            byLinea={assertivenessData?.byLinea || []}
            byFamilia={assertivenessData?.byFamilia || []}
            byGenero={assertivenessData?.byGenero || []}
            byRotacion={assertivenessData?.byRotacion || []}
            globalExactitud={assertivenessData?.global.exactitud ?? null}
            rawProducts={assertivenessRawData || []}
            magnitud={assertivenessData?.global.magnitud ?? null}
            loading={assertivenessLoading}
            efficiencyData={efficiencyData || []}
            efficiencyLoading={efficiencyLoading}
            onOpenBI={() => handleOpenMetric('bi')}
            onBackToAnalytics={() => { setViewMode('analytics'); setActiveMetric('exactitud'); }}
          />
        );
      case 'lineProducts':
        const currentColumns = selectedLinea?.isCounted ? COUNTED_LINE_PRODUCT_COLUMNS : BASIC_PRODUCT_COLUMNS;
        return (
          <ProductTable
            products={products}
            onBack={resetView}
            viewTitle={`Productos de la línea: ${selectedLinea?.Linea}`}
            columns={currentColumns}
            onAdjustLineClick={handleLineaAjustada}
            showAdjustButton={selectedLinea?.isCounted}
          />
        );
      case 'allProducts':
        return (
          <ProductTable
            products={products}
            onBack={resetView}
            viewTitle="Todos los Productos Contados"
            columns={ALL_COUNTED_PRODUCTS_COLUMNS}
            onAdjustLineClick={handleLineaAjustada}
            showAdjustButton={false}
            onDownload={exportTableDataToCsv} // Pasar la función de descarga
          />
        );
      case 'lines':
      default:
        return (
          <div className="lineas-grid">
            {lineas.map((linea, index) => (
              <LineaCard key={index} linea={linea} onClick={handleLineaClick} />
            ))}
          </div>
        );
    }
  };

  return (
    <div className="inventario-details-container">
      <div className="inventario-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
          <h3>Inventario ID: {inventario.InventarioID}</h3>
          <div style={{ display: 'flex', gap: '20px', fontSize: '0.85rem', color: '#666' }}>
            <span><strong>Auditor:</strong> {inventario.Auditor}</span>
            <span><strong>Ciudad:</strong> {inventario.Ciudad}</span>
          </div>
        </div>
        
        <div className="metrics-grid">
          <MetricCard 
            label="Progreso" 
            value={`${inventario.ProgressPorcentage}%`} 
            icon={<BiBarChart />} 
            colorClass="metric-blue"
          />
          <MetricCard 
            label="Asertividad" 
            value={assertivenessLoading ? "..." : `${(assertivenessData?.global.exactitud ?? 0).toFixed(1)}%`}
            icon={<BiTargetLock />} 
            colorClass="metric-green"
            onClick={() => handleOpenMetric('exactitud')}
            isActive={viewMode === 'analytics' && activeMetric === 'exactitud'}
            loading={assertivenessLoading}
          />
          <MetricCard 
            label="Diferencias" 
            value={assertivenessLoading ? "..." : `${assertivenessData?.global.diferencias ?? 0} art.`}
            icon={<BiTrendingDown />} 
            colorClass="metric-red"
            onClick={() => handleOpenMetric('diferencias')}
            isActive={viewMode === 'diferencias'}
            loading={assertivenessLoading}
          />
          <MetricCard 
            label="COSTO INV." 
            value={assertivenessLoading ? "..." : formatMoney(assertivenessData?.global.magnitud?.balance?.monto ?? 0)}
            icon={<BiDollarCircle />} 
            colorClass={(assertivenessData?.global.magnitud?.balance?.monto ?? 0) >= 0 ? 'metric-green' : 'metric-red'}
            onClick={() => handleOpenMetric('magnitud')}
            isActive={viewMode === 'magnitud'}
            loading={assertivenessLoading}
          />
          <MetricCard 
            label="Eficiencia" 
            value={efficiencyLoading ? "..." : (() => {
              const grouped = (efficiencyData || []).reduce((acc, row) => {
                if (!row.linea) return acc;
                if (!acc[row.linea]) acc[row.linea] = { productos: 0, horasActivas: 0 };
                acc[row.linea].productos += Number(row.productos || 0);
                acc[row.linea].horasActivas += Number(row.horasActivas || 0);
                return acc;
              }, {});
              const rows = Object.values(grouped);
              const totalProd = rows.reduce((s, r) => s + r.productos, 0);
              const totalHrs = rows.reduce((s, r) => s + r.horasActivas, 0);
              if (totalHrs <= 0) return "—";
              return `${(totalProd / totalHrs).toFixed(1)} SKU/hr`;
            })()}
            icon={<BiTimeFive />} 
            colorClass="metric-orange"
            onClick={() => handleOpenMetric('eficiencia')}
            isActive={viewMode === 'eficiencia'}
            loading={efficiencyLoading}
            style={{ display: 'none' }}
          />
        </div>
      </div>

      {viewMode === "lines" && (
        <div className="navigation-buttons">
          <button className="nav-button" onClick={handleViewAllProducts}>
            Ver Todos Productos Contados
          </button>
          <h4>Líneas</h4>
          <button style={{width: "100%"}} onClick={onBack} className="back-button">
            <BiArrowBack /> Regresar a Inventarios
          </button>
        </div>
      )}

      {viewMode === "lineProducts" ? (
        <div style={{ display: "flex", width: "100%" }}>
          <div className="lineas-scroll-container" style={{ width: "70%" }}>
            {renderContent()}
          </div>
          <div
            style={{
              width: "30%",
              padding: "20px",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
            }}
          >
            {selectedLinea && (
              <img
                src={`${process.env.PUBLIC_URL}/Perfiles/${selectedLinea.Linea}.jpg`}
                alt={`Imagen de la línea ${selectedLinea.Linea}`}
                style={{
                  width: "80%",
                  maxHeight: "300px",
                  height: "auto",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  padding: "5px",
                }}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            )}
          </div>
        </div>
      ) : (
        <div className="lineas-scroll-container">{renderContent()}</div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h4>Confirmación de Ajuste de Línea</h4>
            <p>
              Estás seguro de que deseas ajustar el estatus de esta línea y
              descargar sus datos?
            </p>
            <div className="modal-actions">
              <button
                onClick={handleCancelAdjustment}
                className="modal-button cancel-button"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmAdjustment}
                className="modal-button confirm-button"
              >
                Aceptar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


const InventarioCard = ({ inventario, onViewDetails }) => {
  const {
    InventarioID,
    Fecha,
    qtyProductos,
    qtyLineas,
    Almacen,
    Ciudad,
    ProgressPorcentage,
    Auditor,
  } = inventario;
  const formattedDate = Fecha ? new Date(Fecha).toLocaleDateString() : "N/A";

  return (
    <button
      className="inventario-card"
      onClick={() => onViewDetails(inventario)}
    >
      <div className="card-icon">
        <BiBox size={24} color="#007bff" />
      </div>
      <div className="card-info">
        <h3>Inventario ID: {InventarioID}</h3>
        <p>
          <strong>Fecha de Alta:</strong> {formattedDate}
        </p>
        <p>
          <strong>Productos a Contar:</strong> {qtyProductos}
        </p>
        <p>
          <strong>Líneas a Contar:</strong> {qtyLineas}
        </p>
        <p>
          <strong>Almacén:</strong> {Almacen}
        </p>
        <p>
          <strong>Ciudad:</strong> {Ciudad}
        </p>
        <p>
          <strong>Auditor:</strong> {Auditor}
        </p>
      </div>
      <div className="card-progress">
        <div className="progress-bar-container">
          <div
            className="progress-bar-fill"
            style={{ width: `${ProgressPorcentage}%` }}
          ></div>
        </div>
        <span className="progress-text">{ProgressPorcentage}% Completado</span>
      </div>
    </button>
  );
};

const InventariosActivos = () => {
  const [inventarios, setInventarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedInventario, setSelectedInventario] = useState(null);

  // Función para cargar los inventarios activos, ahora como useCallback
  const fetchInventarios = useCallback(async () => {
    setLoading(true);
    setError(null);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
      setError("La solicitud ha tardado demasiado y ha sido cancelada.");
      setLoading(false);
    }, 8000); // 8 seconds timeout

    try {
      const response = await fetch(
        `${process.env.REACT_APP_URL_API1}/getresumeninventariosweb`,
        { signal: controller.signal }
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (Array.isArray(data)) {
        // Ordenar de más antiguo a más reciente
        data.sort((a, b) => new Date(a.Fecha) - new Date(b.Fecha));
        setInventarios(data);
      } else {
        setInventarios([]);
      }
    } catch (e) {
      if (e.name === "AbortError") {
        console.log("Fetch abortado");
      } else {
        console.error("Error fetching inventarios:", e);
        setError(
          "No se pudieron cargar los inventarios. Inténtalo de nuevo más tarde."
        );
      }
    } finally {
      // Solo establecemos loading a false si la solicitud no fue abortada por timeout
      // La lógica del timeout ya maneja el loading a false si se aborta.
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, []); // No dependencies for the fetch logic itself, as it manages its own AbortController

  // Carga inicial de inventarios al montar el componente
  useEffect(() => {
    fetchInventarios();
    // No necesitamos una función de limpieza específica aquí para AbortController
    // porque fetchInventarios() ya gestiona su propio controller y timeout por llamada.
  }, [fetchInventarios]); // Depend on fetchInventarios to re-run when its dependencies change

  const handleViewDetails = (inventario) => {
    setSelectedInventario(inventario);
  };

  // Modificado para refrescar las tarjetas de inventarios
  const handleBack = () => {
    setSelectedInventario(null); // Vuelve a la vista de tarjetas de inventarios
    fetchInventarios(); // Refresca los datos de los inventarios
  };

  if (loading) {
    return <div className="loading-message">Cargando inventarios...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  if (selectedInventario) {
    return (
      <InventarioDetails
        inventario={selectedInventario}
        onBack={handleBack}
      />
    );
  }

  if (inventarios.length === 0) {
    return (
      <div className="no-data-message">
        No hay inventarios registrados
      </div>
    );
  }

  return (
    <div className="inventarios-activos-container">
      <div className="inventario-scroll-container">
        <div className="inventario-cards-grid">
          {inventarios.map((inventario) => (
            <InventarioCard
              key={inventario.InventarioID}
              inventario={inventario}
              onViewDetails={handleViewDetails}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default InventariosActivos;